import { FC, MouseEventHandler, useCallback, useMemo, useState } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import {
  PickDtoFilter,
  PickDtoIsRequired,
  PickDtoOnChange,
} from '@/shared/lib/forms/form-wrapper.types';
import { CollectionName, useSJDatabase } from '@/shared/lib/sj-orm';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { FieldSelectView } from '@/shared/uikitV2/Select/FieldSelectView';
import { useModalsStore } from '@/widgets/modals';

type PickOneOfDtosProps = {
  label: string;
  required?: boolean | string;
  fixedCollection: CollectionName.CONTACTS | CollectionName.BENEFICIARIES;
  dtoTypes: string[];
  fieldName: string;
  disabled?: boolean;
  excludeIds?: DtoId[];
  filter?: PickDtoFilter;
  isRequired?: PickDtoIsRequired;
  onValueChange?: PickDtoOnChange;
  onDelete?: (fieldName: string) => void;
};

export const ALLOWED_PICK_ONE_ENTITY = [
  CollectionName.CONTACTS,
  CollectionName.BENEFICIARIES,
] as const;

export const PickOneOfDtos: FC<PickOneOfDtosProps> = ({
  label,
  required,
  fixedCollection,
  dtoTypes,
  fieldName,
  disabled,
  filter,
  isRequired,
}) => {
  const sjDb = useSJDatabase();
  const modals = useModalsStore();
  const [isSelectEntitiesModalOpen, setIsSelectEntitiesModalOpen] = useState(false);
  const { control, setValue } = useFormContext();

  const watchedValue = useWatch({ control, name: fieldName });

  const clearField = useCallback(
    (field: string) => {
      setValue(field, undefined, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const currentLabel = useMemo(() => {
    if (watchedValue === 'me') return 'Me';

    if (fixedCollection === CollectionName.CONTACTS) {
      return sjDb.collections[fixedCollection]?.findById(watchedValue)?.nickname ?? '';
    }

    if (fixedCollection === CollectionName.BENEFICIARIES) {
      return sjDb.collections[fixedCollection]?.findById(watchedValue)?.nickName ?? '';
    }

    return '';
  }, [fixedCollection, sjDb, watchedValue]);

  const handleOpen = ({ onChange }: ControllerRenderProps<FieldValues, string>) => {
    setIsSelectEntitiesModalOpen(true);

    if (fixedCollection === CollectionName.CONTACTS) {
      modals.openModal({
        type: 'SelectContact',
        selectedValue: watchedValue,
        onSelect: (id) => {
          onChange(id);
          setIsSelectEntitiesModalOpen(false);
        },
        contactTypes: dtoTypes,
        filter,
        onClose: () => {
          setIsSelectEntitiesModalOpen(false);
        },
      });
    }

    if (fixedCollection === CollectionName.BENEFICIARIES) {
      modals.openModal({
        type: 'SelectBeneficiary',
        selectedValue: watchedValue,
        onSelect: (id) => {
          onChange(id);
          setIsSelectEntitiesModalOpen(false);
        },
        beneficiaryGroups: dtoTypes,
        filter,
        onClose: () => {
          setIsSelectEntitiesModalOpen(false);
        },
      });
    }
  };

  const handleReset: MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      clearField(fieldName);
    },
    [clearField, fieldName],
  );

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{ required: isRequired ? isRequired(fieldName, watchedValue) : required }}
      render={({ field, fieldState }) => (
        <FieldSelectView
          onFieldClick={() => handleOpen(field)}
          onClickReset={handleReset}
          label={label}
          selectedView={currentLabel}
          open={isSelectEntitiesModalOpen}
          disabled={disabled}
          required={Boolean(required)}
          status={fieldState.error ? 'error' : 'default'}
        />
      )}
    />
  );
};
